import React from 'react'
import styled from '@emotion/styled'
import Link from 'next/link'
import { createEventDateFormatter, getLinkProps } from '~/utils'
import {
  Avatar,
  Button,
  H3,
  H4,
  color,
  device,
  space,
  shadow,
  radius,
  transition,
} from '@ticketswap/solar'
import { Location, Event } from '@ticketswap/comets'
import { useLanguage } from '../LanguageContext'

export const Menu = styled.div`
  text-align: left;
  overflow: hidden;
  background-color: white;

  @media ${device.tablet} {
    position: absolute;
    left: 0;
    right: 0;
    padding: 0;
    border: 0;
    top: calc(100% + ${space[4]});
    border-radius: ${radius.md};
    box-shadow: ${shadow.strong};
  }
`

export const MenuBody = styled.div`
  padding: ${space[16]};
  display: grid;
  grid-gap: ${space[16]};
  border-top: ${props =>
    props.isOpen ? `1px solid ${color.spaceLightest}` : 0};

  @media ${device.tablet} {
    border: 0;
    padding: ${space[16]} ${space[24]} ${space[24]};
  }
`

export const MenuFooter = styled.div`
  border-top: 1px solid ${color.spaceLightest};
`

export const ShowMoreLink = styled.a`
  text-align: center;
  height: ${space[56]};
  display: flex;
  justify-content: center;
  align-items: center;

  color: ${color.earth};
  transition: color ${transition};
  cursor: pointer;

  &:hover,
  &:focus {
    color: ${color.space};
  }
`

const ItemContainer = styled.div`
  position: relative;
  color: ${props => (props.highlighted ? color.space : color.earth)};
  padding-left: ${40 / 16}rem;
  transition: color ${transition};
  cursor: pointer;
`

const ItemAdornment = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: translate3d(-3px, -1px, 0);

  @media ${device.tablet} {
    transform: translate3d(-3px, 2px, 0);
  }
`

const ItemDetails = styled.span`
  color: ${color.spaceLight};
`

export const Item = ({ node: { __typename: type, ...node }, t, ...props }) => {
  const language = useLanguage()
  const formatEventDate = createEventDateFormatter(language)
  const eventTime = formatEventDate(node.startDate, node.endDate)
  let artistInfo = t('search.artist')
  let venueInfo = t('search.venue')
  let cityInfo = t('search.city')
  let eventInfo = eventTime
  if (node.name) {
    cityInfo = `${cityInfo} • ${node.name}`
  }
  if (node.locationName) {
    eventInfo = `${eventInfo} • ${node.locationName}`
  }
  if (node.cityName) {
    venueInfo = `${venueInfo} • ${node.cityName}`
    eventInfo = `${eventInfo}, ${node.cityName}`
  }
  if (node.country && node.country.code) {
    venueInfo = `${venueInfo}, ${node.country.code}`
    cityInfo = `${cityInfo}, ${node.country.code}`
    eventInfo = `${eventInfo}, ${node.country.code}`
  }

  return (
    <ItemContainer {...props}>
      <ItemAdornment>
        {type === 'LocationResult' || type === 'CityResult' ? (
          <Location size={24} />
        ) : type === 'ArtistResult' ? (
          <Avatar src={node.avatar} size={24} />
        ) : (
          <Event size={24} />
        )}
      </ItemAdornment>
      <H4>{node.name}</H4>
      <ItemDetails>
        {type === 'ArtistResult'
          ? artistInfo
          : type === 'LocationResult'
          ? venueInfo
          : type === 'CityResult'
          ? cityInfo
          : eventInfo}
      </ItemDetails>
    </ItemContainer>
  )
}

const NothingFoundContainer = styled.div`
  text-align: center;
  padding: ${space[16]};
  color: ${color.space};
`

const NothingFoundInfo = styled.p`
  margin-top: ${space[4]};
  margin-bottom: ${space[16]};
  color: ${color.spaceLight};
`

export const NothingFound = ({ t, query, ...props }) => (
  <NothingFoundContainer>
    <H3>{t('search.nothingFound')}</H3>
    <NothingFoundInfo>{t('search.info')}</NothingFoundInfo>
    <Link {...getLinkProps('ADD_EVENT', query && { title: query })} passHref>
      <Button
        as="a"
        leftAdornment={<Event size={24} />}
        onClick={props.closeSearch}
      >
        {t('search.addNew')}
      </Button>
    </Link>
  </NothingFoundContainer>
)
